import React, { useState, useMemo, useEffect } from 'react';
import { FileQuestion, Plus, Trash2, Star, ArrowUp, ArrowDown, Sparkles } from 'lucide-react';
import { InlineMath } from 'react-katex';
import OptionsList from './OptionsList';
import { default as formatQuestionId } from './formatQuestionId';
import EditableText from './EditableText';
import { toast } from 'react-hot-toast';
import { gradeApi } from '../../../../api';
import { useAuth } from '../../../../auth/AuthContext';

const QuestionCard = ({
  id,
  question,
  extractedQuestions,
  setExtractedQuestions,
  uploadQuestions,
  pdfPageCount, // Added prop for PDF page count
  handleMoveQuestion, // New prop
  questionIndex, // New prop
  totalQuestions, // New prop
  solutionPdfUrl // New prop for solution PDF URL
}) => {
  const [editedQuestion, setEditedQuestion] = useState(question);
  const [editingPageNumber, setEditingPageNumber] = useState(false);
  const [editedPageNumber, setEditedPageNumber] = useState('');
  const [showAiEditPopup, setShowAiEditPopup] = useState(false);
  const [aiEditInput, setAiEditInput] = useState('');
  const [activeSubIndex, setActiveSubIndex] = useState(null);
  const [isProcessingAiEdit, setIsProcessingAiEdit] = useState(false);
  const [applyToAllQuestions, setApplyToAllQuestions] = useState(false);
  const pageNumberInputRef = React.useRef(null);
  const aiEditInputRef = React.useRef(null);
  const { user } = useAuth();

  // Sync editedQuestion with question prop when it changes
  useEffect(() => {
    setEditedQuestion(question);
  }, [question]);

  // Use the provided PDF page count, or fall back to a reasonable default
  const maxPages = pdfPageCount;

  // console.log('Initial question data:', question, 'Edited question:', editedQuestion);

  // Determine if this question has only one subquestion with blank text
  // This creates the abstraction where it looks like just one question with a solution
  const hasSingleBlankSubquestion = useMemo(() => {
    return (
      editedQuestion.subquestions &&
      editedQuestion.subquestions.length === 1 &&
      (!editedQuestion.subquestions[0].text || editedQuestion.subquestions[0].text.trim() === '')
    );
  }, [editedQuestion.subquestions]);

  // Track whether to show subquestions separately even when there's only one blank subquestion
  const [forceShowSubquestions, setForceShowSubquestions] = useState(false);

  const handleSave = updatedQuestion => {
    const updatedExtractedQuestions = {
      ...extractedQuestions,
      [id]: updatedQuestion
    };
    setExtractedQuestions(updatedExtractedQuestions);
    console.log('uploading questions');
    uploadQuestions(updatedExtractedQuestions);
  };

  const handleQuestionTextChange = newText => {
    const updatedQuestion = {
      ...editedQuestion,
      questionText: newText
    };
    setEditedQuestion(updatedQuestion);
    handleSave(updatedQuestion);
  };

  const handleSubMCQOptionChange = (subIndex, optionIndex, isCorrect) => {
    console.log('Changing MCQ option correctness:', subIndex, optionIndex, isCorrect);
    console.log('Current options:', editedQuestion.subquestions[subIndex].options);

    const updatedSubquestions = [...editedQuestion.subquestions];
    const subquestion = updatedSubquestions[subIndex];

    // Make sure options is an array
    if (!Array.isArray(subquestion.options)) {
      console.log('Options is not an array');
      return;
    }

    try {
      // We're using simple string options now, so we directly work with the correctAnswer array
      const letter = String.fromCharCode(65 + optionIndex);
      let correctAnswer = Array.isArray(subquestion.correctAnswer)
        ? [...subquestion.correctAnswer]
        : [];

      if (isCorrect && !correctAnswer.includes(letter)) {
        correctAnswer.push(letter);
      } else if (!isCorrect && correctAnswer.includes(letter)) {
        correctAnswer = correctAnswer.filter(ans => ans !== letter);
      }

      updatedSubquestions[subIndex] = {
        ...subquestion,
        correctAnswer
      };

      const updatedQuestion = {
        ...editedQuestion,
        subquestions: updatedSubquestions
      };
      setEditedQuestion(updatedQuestion);
      handleSave(updatedQuestion);
    } catch (error) {
      console.error('Error updating MCQ option:', error);
    }
  };

  const handleSubMCQOptionTextChange = (subIndex, optionIndex, text) => {
    console.log('Updating MCQ option text:', subIndex, optionIndex, text);

    const updatedSubquestions = [...editedQuestion.subquestions];
    const subquestion = updatedSubquestions[subIndex];

    if (Array.isArray(subquestion.options)) {
      // Simple, direct update - just replace the string at the given index
      const updatedOptions = [...subquestion.options];
      updatedOptions[optionIndex] = text;

      updatedSubquestions[subIndex] = {
        ...subquestion,
        options: updatedOptions
      };
    }

    console.log('Updated subquestion:', updatedSubquestions[subIndex]);

    const updatedQuestion = {
      ...editedQuestion,
      subquestions: updatedSubquestions
    };

    setEditedQuestion(updatedQuestion);
    handleSave(updatedQuestion);
  };

  const handleAddSubMCQOption = subIndex => {
    console.log('Adding MCQ option to subquestion', subIndex);

    const updatedSubquestions = [...editedQuestion.subquestions];
    const subquestion = updatedSubquestions[subIndex];

    // Check if options array exists and has valid format
    if (Array.isArray(subquestion.options) && subquestion.options.length > 0) {
      const option = subquestion.options[0];

      // Convert existing options to strings and add a new empty string option
      console.log('Adding new option as a string');

      // First convert any existing options to strings if they're not already
      const standardizedOptions = subquestion.options.map(opt => {
        if (typeof opt === 'string') {
          return opt;
        }
        if (typeof opt === 'object' && opt !== null) {
          if (opt.hasOwnProperty('text')) {
            return opt.text;
          }
          if (opt.hasOwnProperty('0')) {
            return opt['0'];
          }
        }
        return '';
      });

      // Add a new empty option
      updatedSubquestions[subIndex] = {
        ...subquestion,
        options: [...standardizedOptions, '']
      };
    } else {
      // Initialize with simple string format
      console.log('Initializing options array with simple string format');
      updatedSubquestions[subIndex] = {
        ...subquestion,
        options: ['Option A', 'Option B']
      };
    }

    // Save the updated question
    const updatedQuestion = {
      ...editedQuestion,
      subquestions: updatedSubquestions
    };

    setEditedQuestion(updatedQuestion);
    handleSave(updatedQuestion);
  };

  const handleDeleteSubMCQOption = (subIndex, optionIndex) => {
    console.log('Deleting MCQ option:', subIndex, optionIndex);
    console.log('Current options:', editedQuestion.subquestions[subIndex].options);

    // Make deep copy to ensure we're not working with references
    const updatedSubquestions = JSON.parse(JSON.stringify(editedQuestion.subquestions));
    const subquestion = updatedSubquestions[subIndex];

    if (subquestion.options && Array.isArray(subquestion.options)) {
      // Make sure there are more than 2 options before allowing deletion
      if (subquestion.options.length <= 2) {
        console.log('Minimum 2 options required, not deleting');
        return;
      }

      // Remove option at exact index
      console.log('Removing option at index:', optionIndex);
      const updatedOptions = [...subquestion.options];
      updatedOptions.splice(optionIndex, 1);

      console.log('Options before deletion:', subquestion.options);
      console.log('Options after deletion:', updatedOptions);

      // Update the subquestion with new options
      updatedSubquestions[subIndex] = {
        ...subquestion,
        options: updatedOptions
      };

      // Create the updated question object
      const updatedQuestion = {
        ...editedQuestion,
        subquestions: updatedSubquestions
      };

      // Save changes
      console.log('Final updated question:', updatedQuestion);
      setEditedQuestion(updatedQuestion);
      handleSave(updatedQuestion);
    }
  };

  const handleMCQOptionChange = (optionIndex, isCorrect) => {
    console.log(optionIndex, isCorrect);
    console.log('Changing MCQ option correctness:', optionIndex, isCorrect);

    // Ensure options exists
    if (!editedQuestion.options || !Array.isArray(editedQuestion.options)) {
      console.log('options does not exist or is not an array');
      return;
    }

    // Generate the letter for this option (A, B, C, etc.)
    const letter = String.fromCharCode(65 + optionIndex);

    // Update correctAnswer array to reflect the current selection
    let correctAnswer = Array.isArray(editedQuestion.correctAnswer)
      ? [...editedQuestion.correctAnswer]
      : [];

    if (isCorrect && !correctAnswer.includes(letter)) {
      // Add this letter to correct answers
      correctAnswer.push(letter);
    } else if (!isCorrect && correctAnswer.includes(letter)) {
      // Remove this letter from correct answers
      correctAnswer = correctAnswer.filter(ans => ans !== letter);
    }

    console.log('Updated correctAnswer array:', correctAnswer);

    // Save the updated question with the modified correctAnswer
    const updatedQuestion = {
      ...editedQuestion,
      correctAnswer
    };

    setEditedQuestion(updatedQuestion);
    handleSave(updatedQuestion);
  };

  const handleMCQOptionTextChange = (optionIndex, text) => {
    console.log('Changing MCQ option text:', optionIndex, text);

    // Ensure options exists
    if (!editedQuestion.options || !Array.isArray(editedQuestion.options)) {
      console.log('options does not exist or is not an array');
      return;
    }

    // Simply replace the string at the specified index
    const updatedOptions = [...editedQuestion.options];
    updatedOptions[optionIndex] = text;

    console.log('Updated options after text change:', updatedOptions);

    // Save the updated question with the modified option text
    const updatedQuestion = {
      ...editedQuestion,
      options: updatedOptions
    };

    setEditedQuestion(updatedQuestion);
    handleSave(updatedQuestion);
  };

  const handleAddMCQOption = () => {
    console.log('Adding MCQ option, current options:', editedQuestion.options);
    // Ensure options exists
    if (!editedQuestion.options) {
      console.log('options does not exist, initializing');
      const updatedQuestion = {
        ...editedQuestion,
        options: ['Option A', 'Option B'],
        correctAnswer: ['A'] // Initialize with first option as correct
      };
      setEditedQuestion(updatedQuestion);
      handleSave(updatedQuestion);
      return;
    }

    const updatedOptions = [...(editedQuestion.options || [])];
    updatedOptions.push('');
    console.log('Updated options:', updatedOptions);
    const updatedQuestion = {
      ...editedQuestion,
      options: updatedOptions
    };
    setEditedQuestion(updatedQuestion);
    handleSave(updatedQuestion);
  };

  const handleDeleteMCQOption = optionIndex => {
    // Ensure we have more than 2 options before allowing deletion
    if (!editedQuestion.options || editedQuestion.options.length <= 2) {
      return;
    }

    const updatedOptions = [...editedQuestion.options];
    updatedOptions.splice(optionIndex, 1);
    const updatedQuestion = {
      ...editedQuestion,
      options: updatedOptions
    };
    setEditedQuestion(updatedQuestion);
    handleSave(updatedQuestion);
  };

  // Function to add a subquestion
  const handleAddSubquestion = () => {
    // If we have a single blank subquestion abstraction, first show it separately
    if (hasSingleBlankSubquestion && !forceShowSubquestions) {
      setForceShowSubquestions(true);
      return;
    }

    const newSubquestions = [...editedQuestion.subquestions];
    const nextLetter = String.fromCharCode(97 + newSubquestions.length);

    newSubquestions.push({
      letter: nextLetter,
      text: '',
      type: 'freeform',
      solution: '',
      options: [],
      correctAnswer: null,
      page_number: 1 // Default page number for new subquestions
    });

    setEditedQuestion({
      ...editedQuestion,
      subquestions: newSubquestions
    });

    // Always show subquestions when we add one
    if (!forceShowSubquestions) {
      setForceShowSubquestions(true);
    }
  };

  // New function to delete a subquestion
  const handleDeleteSubquestion = subIndex => {
    const newSubquestions = [...editedQuestion.subquestions];

    // Remove the subquestion at the specified index
    newSubquestions.splice(subIndex, 1);

    // Update the letters for all subsequent subquestions
    for (let i = subIndex; i < newSubquestions.length; i++) {
      newSubquestions[i].letter = String.fromCharCode(97 + i);
    }

    const updatedQuestion = {
      ...editedQuestion,
      subquestions: newSubquestions
    };

    // Update component state
    setEditedQuestion(updatedQuestion);

    // Save changes and trigger the constructor update
    handleSave(updatedQuestion);

    console.log('Subquestion deleted and saved');
  };

  // Function to change subquestion type (e.g., from freeform to MCQ)
  const handleSubquestionTypeChange = (subIndex, newType) => {
    const newSubquestions = [...editedQuestion.subquestions];
    newSubquestions[subIndex].type = newType;

    // Initialize options if changing to MCQ type - using simple strings
    if (
      newType === 'mcq' &&
      (!newSubquestions[subIndex].options || newSubquestions[subIndex].options.length === 0)
    ) {
      newSubquestions[subIndex].options = ['Option A', 'Option B'];
      // Initialize correctAnswer to track which options are correct
      newSubquestions[subIndex].correctAnswer = ['A'];
    }

    const updatedQuestion = {
      ...editedQuestion,
      subquestions: newSubquestions
    };

    // Update component state
    setEditedQuestion(updatedQuestion);

    // Save changes and trigger constructor update
    handleSave(updatedQuestion);

    console.log('Subquestion type changed to', newType, 'and saved');
  };

  // Function to toggle multiple correct answers for a subquestion
  const toggleSubMultipleCorrect = subIndex => {
    const newSubquestions = [...editedQuestion.subquestions];
    const subquestion = newSubquestions[subIndex];

    subquestion.allowMultipleCorrect = !subquestion.allowMultipleCorrect;

    // If turning off multiple correct and multiple are selected, keep only the first correct one
    if (!subquestion.allowMultipleCorrect) {
      const correctIndices = subquestion.options
        .map((opt, idx) => (opt.isCorrect ? idx : -1))
        .filter(idx => idx !== -1);

      if (correctIndices.length > 1) {
        subquestion.options = subquestion.options.map((opt, idx) => ({
          ...opt,
          isCorrect: idx === correctIndices[0]
        }));
      }
    }

    const updatedQuestion = {
      ...editedQuestion,
      subquestions: newSubquestions
    };

    // Update state and save changes
    setEditedQuestion(updatedQuestion);
    handleSave(updatedQuestion);

    console.log('Multiple correct answer toggle updated and saved');
  };

  // New function to delete the entire question
  const handleDeleteQuestion = () => {
    // Make a copy of the current questions
    const updatedExtractedQuestions = { ...extractedQuestions };
    // Delete the current question
    delete updatedExtractedQuestions[id];

    // Set the updated questions without renumbering
    setExtractedQuestions(updatedExtractedQuestions);
    uploadQuestions(updatedExtractedQuestions);
  };

  // Handle page number edit start
  const handlePageNumberClick = currentPageNumber => {
    setEditingPageNumber(true);
    setEditedPageNumber(currentPageNumber || '1');
    setTimeout(() => {
      if (pageNumberInputRef.current) {
        pageNumberInputRef.current.focus();
        pageNumberInputRef.current.select();
      }
    }, 50);
  };

  // Handle saving the page number
  const handlePageNumberSave = () => {
    // Clamp the page number between 1 and maxPages
    const pageNumber = parseInt(editedPageNumber) || 1;
    const clampedPageNumber = Math.min(Math.max(1, pageNumber), maxPages);
    console.log(maxPages);

    const updatedSubquestions = [...editedQuestion.subquestions];
    updatedSubquestions[0].page_number = clampedPageNumber;

    const updatedQuestion = {
      ...editedQuestion,
      subquestions: updatedSubquestions
    };

    setEditedQuestion(updatedQuestion);
    handleSave(updatedQuestion);
    setEditingPageNumber(false);
  };

  // Handle key press in input field
  const handlePageNumberKeyDown = e => {
    if (e.key === 'Enter') {
      handlePageNumberSave();
    } else if (e.key === 'Escape') {
      setEditingPageNumber(false);
    }
  };

  // Function to handle AI edit popup
  const handleAiEditClick = subIndex => {
    setActiveSubIndex(subIndex);
    setShowAiEditPopup(true);
    // Don't clear the input - keep previous instructions
    setApplyToAllQuestions(false);

    // Focus the textarea when the popup opens
    setTimeout(() => {
      if (aiEditInputRef.current) {
        aiEditInputRef.current.focus();
      }
    }, 100);
  };

  const handleAiEditConfirm = async () => {
    if (!aiEditInput.trim()) {
      setShowAiEditPopup(false);
      return;
    }

    try {
      setIsProcessingAiEdit(true);

      let payload;

      // Function to convert PDF URL to base64
      const convertPdfToBase64 = async pdfUrl => {
        if (!pdfUrl) {
          return null;
        }

        try {
          // If it's already a base64 string, extract the data part
          if (pdfUrl.startsWith('data:application/pdf;base64,')) {
            return pdfUrl.split(',')[1];
          }

          // If it's a URL, fetch it and convert to base64
          const response = await fetch(pdfUrl);
          const blob = await response.blob();

          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const base64String = reader.result;
              if (base64String.startsWith('data:application/pdf;base64,')) {
                resolve(base64String.split(',')[1]);
              } else {
                reject(new Error('Invalid PDF format'));
              }
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        } catch (error) {
          console.error('Error converting PDF to base64:', error);
          return null;
        }
      };

      // Convert solution PDF to base64
      const pdfBase64 = await convertPdfToBase64(solutionPdfUrl);

      if (applyToAllQuestions) {
        // Send all questions to backend
        const allQuestions = [];
        Object.values(extractedQuestions).forEach(q => {
          q.subquestions.forEach(sq => {
            allQuestions.push({
              questionText: q.questionText,
              solution: sq.solution,
              weight: sq.weight
            });
          });
        });

        payload = {
          instruction: aiEditInput,
          questions: allQuestions,
          ...(pdfBase64 && { pdf_base64: pdfBase64 })
        };
      } else {
        // Get the current subquestion's rubric items
        const subquestion = editedQuestion.subquestions[activeSubIndex];

        // Prepare the payload for the API request
        payload = {
          instruction: aiEditInput,
          questionText: editedQuestion.questionText,
          solution: subquestion.solution,
          weight: subquestion.weight,
          ...(pdfBase64 && { pdf_base64: pdfBase64 })
        };
      }

      // Call API to get AI enhanced rubric items
      const response = await gradeApi.post({
        path: '/api/ai/enhance-rubric/',
        body: payload,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });

      // Check if we got a valid response
      if (response.data && response.data.gradingCriteria) {
        // If applying to all questions
        if (applyToAllQuestions) {
          // Backend returns an array of results for each question
          const results = response.data.gradingCriteria;

          // Update all questions with their corresponding results
          const updatedExtractedQuestions = { ...extractedQuestions };
          let resultIndex = 0;

          Object.keys(updatedExtractedQuestions).forEach(qId => {
            const question = updatedExtractedQuestions[qId];
            let totalWeight = 0;
            question.subquestions.forEach((sq, sqIndex) => {
              if (resultIndex < results.length && results[resultIndex]) {
                const newWeight = Number(results[resultIndex].questionWeight);
                sq.solution = results[resultIndex].gradingCriteria || sq.solution;
                sq.weight = newWeight || sq.weight;
                sq.max_weight = newWeight || sq.max_weight;
              }
              totalWeight += sq.weight || 0;
              resultIndex++;
            });
            question.weight = totalWeight;
          });

          setExtractedQuestions(updatedExtractedQuestions);
          uploadQuestions(updatedExtractedQuestions);

          // Also update the current question if it was modified
          const currentQuestionUpdated = updatedExtractedQuestions[id];
          if (currentQuestionUpdated) {
            setEditedQuestion(currentQuestionUpdated);
          }
          toast.success('Applied AI enhancements to all questions!');
        } else {
          // Update the rubric items in the question state (original functionality)
          const updatedSubquestions = [...editedQuestion.subquestions];
          const newWeight = Number(response.data.questionWeight);

          updatedSubquestions[activeSubIndex] = {
            ...updatedSubquestions[activeSubIndex],
            solution: response.data.gradingCriteria,
            weight: newWeight,
            max_weight: newWeight
          };

          const totalWeight = updatedSubquestions.reduce((sum, sq) => sum + (sq.weight || 0), 0);

          const updatedQuestion = {
            ...editedQuestion,
            subquestions: updatedSubquestions,
            weight: totalWeight
          };

          // Update state and save changes
          setEditedQuestion(updatedQuestion);

          // Also update the global extractedQuestions state
          const updatedExtractedQuestions = {
            ...extractedQuestions,
            [id]: updatedQuestion
          };
          setExtractedQuestions(updatedExtractedQuestions);
          uploadQuestions(updatedExtractedQuestions);

          toast.success('Grading criteria updated successfully!');
        }
      } else {
        toast.error('Failed to update grading criteria. Please try again.');
      }
    } catch (error) {
      console.error('Error while processing AI edit:', error);
      toast.error('Failed to process your request. Please try again.');
    } finally {
      setIsProcessingAiEdit(false);
      setShowAiEditPopup(false);
      // Keep the AI edit input text for future use - don't clear it
    }
  };

  const QuestionText = ({ text, onSave }) => {
    return (
      <div className="question-text-wrapper">
        <FileQuestion className="question-icon" />
        <EditableText
          text={text}
          className="question-text"
          placeholder="Enter question text..."
          multiline={true}
          onSave={onSave}
        />
      </div>
    );
  };

  return (
    <div id={`question-${id}`} className="question-card">
      <div className="question-header">
        <div className="header-content" style={{ display: 'flex', alignItems: 'center' }}>
          <h3>
            <span className="question-number">{formatQuestionId(id)}</span>
            {editedQuestion.weight && (
              <span
                className="question-weight"
                style={{
                  marginLeft: '10px',
                  fontSize: '0.9rem',
                  color: '#666',
                  fontWeight: 'normal',
                  backgroundColor: '#f3f4f6',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  display: 'inline-flex',
                  alignItems: 'center'
                }}
              >
                <Star className="w-3 h-3" style={{ marginRight: '4px', color: '#e4b17a' }} />
                {editedQuestion.weight} {editedQuestion.weight === 1 ? 'point' : 'points'}
              </span>
            )}
          </h3>
          <div
            className="reorder-buttons"
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              marginLeft: '10px',
              marginRight: '10px',
              minHeight: '40px'
            }}
          >
            {questionIndex > 0 ? (
              <button
                className="reorder-btn"
                aria-label="Move question up"
                style={{ padding: '2px', lineHeight: '1', marginBottom: '4px' }}
                onClick={() => handleMoveQuestion(id, 'up')}
              >
                <ArrowUp size={16} />
              </button>
            ) : (
              <div style={{ height: '20px', width: '20px', marginBottom: '4px' }} />
            )}
            {questionIndex < totalQuestions - 1 ? (
              <button
                className="reorder-btn"
                aria-label="Move question down"
                style={{ padding: '2px', lineHeight: '1' }}
                onClick={() => handleMoveQuestion(id, 'down')}
              >
                <ArrowDown size={16} />
              </button>
            ) : (
              <div style={{ height: '20px', width: '20px' }} />
            )}
          </div>
          <div className="action-buttons" style={{ marginLeft: 'auto' }}>
            <button
              className="delete-btn"
              aria-label="Delete question"
              onClick={handleDeleteQuestion}
            >
              <div className="icon-wrapper">
                <Trash2 className="w-4 h-4" />
              </div>
            </button>
          </div>
        </div>
      </div>

      <div className="question-content">
        <QuestionText text={editedQuestion.questionText} onSave={handleQuestionTextChange} />
        {editedQuestion.type === 'mcq' && (
          <OptionsList
            options={editedQuestion.options || []}
            correctAnswer={
              editedQuestion.options
                ?.map((opt, idx) => (opt.isCorrect ? String.fromCharCode(65 + idx) : null))
                ?.filter(Boolean) || []
            }
            onOptionTextChange={handleMCQOptionTextChange}
            onOptionCorrectChange={handleMCQOptionChange}
            onAddOption={handleAddMCQOption}
            onDeleteOption={handleDeleteMCQOption}
          />
        )}
        {editedQuestion.subquestions && (
          <>
            {/* If there's only one blank subquestion and we're not forcing to show them,
                            render it as a direct solution to the main question */}
            {hasSingleBlankSubquestion && !forceShowSubquestions ? (
              editedQuestion.subquestions[0].type === 'mcq' ? (
                <div className="mcq-options-container" id={`solution-${id}-0`}>
                  {editedQuestion.subquestions[0].weight && (
                    <div
                      className="mcq-weight"
                      style={{
                        marginBottom: '10px',
                        fontSize: '0.85rem',
                        color: '#666',
                        fontWeight: 'normal',
                        backgroundColor: '#f3f4f6',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        width: 'fit-content'
                      }}
                    >
                      <Star className="w-3 h-3" style={{ marginRight: '4px', color: '#e4b17a' }} />
                      {editedQuestion.subquestions[0].weight}{' '}
                      {editedQuestion.subquestions[0].weight === 1 ? 'point' : 'points'}
                    </div>
                  )}
                  {/* Page number badge for MCQ type */}
                  {editingPageNumber ? (
                    <div
                      className="page-number-edit"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: '#f3f4f6',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        marginBottom: '10px',
                        marginLeft: '8px'
                      }}
                    >
                      <span
                        style={{
                          fontSize: '0.85rem',
                          color: '#666',
                          marginRight: '4px'
                        }}
                      >
                        Page
                      </span>
                      <input
                        ref={pageNumberInputRef}
                        type="number"
                        min="1"
                        max={maxPages}
                        className="page-number-input"
                        value={editedPageNumber}
                        style={{
                          width: '40px',
                          height: '24px',
                          fontSize: '0.85rem',
                          padding: '2px 4px',
                          borderRadius: '4px',
                          border: '1px solid #ccc',
                          backgroundColor: '#fff'
                        }}
                        onChange={e => {
                          // Allow empty value for input flexibility
                          setEditedPageNumber(e.target.value);
                        }}
                        onKeyDown={handlePageNumberKeyDown}
                        onBlur={handlePageNumberSave}
                      />
                    </div>
                  ) : (
                    <div
                      className="page-number-badge"
                      style={{
                        marginBottom: '10px',
                        marginLeft: '8px',
                        fontSize: '0.85rem',
                        color: '#666',
                        fontWeight: 'normal',
                        backgroundColor: '#f3f4f6',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        width: 'fit-content',
                        cursor: 'pointer'
                      }}
                      onClick={() =>
                        handlePageNumberClick(editedQuestion.subquestions[0].page_number)
                      }
                    >
                      Page {editedQuestion.subquestions[0].page_number || 1}
                    </div>
                  )}
                  <OptionsList
                    options={editedQuestion.subquestions[0].options || []}
                    correctAnswer={editedQuestion.subquestions[0].correctAnswer || []}
                    subIndex={0}
                    onOptionTextChange={handleSubMCQOptionTextChange}
                    onOptionCorrectChange={handleSubMCQOptionChange}
                    onAddOption={() => handleAddSubMCQOption(0)}
                    onDeleteOption={(_, optionIndex) => handleDeleteSubMCQOption(0, optionIndex)}
                  />
                </div>
              ) : (
                <div className="solution-wrapper" id={`solution-${id}-0`}>
                  <div className="solution">
                    <div className="solution-header">
                      <Star className="w-4 h-4" />
                      <span>Grading Criteria</span>
                      {/* Display page number in solution header for single subquestion */}
                      {editingPageNumber ? (
                        <div
                          className="page-number-edit"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: '#f3f4f6',
                            padding: '2px 6px',
                            borderRadius: '4px'
                          }}
                        >
                          <span
                            style={{
                              fontSize: '0.85rem',
                              color: '#666',
                              marginRight: '4px'
                            }}
                          >
                            Page
                          </span>
                          <input
                            ref={pageNumberInputRef}
                            type="number"
                            min="1"
                            max={maxPages}
                            className="page-number-input"
                            value={editedPageNumber}
                            style={{
                              width: '40px',
                              height: '24px',
                              fontSize: '0.85rem',
                              padding: '2px 4px',
                              borderRadius: '4px',
                              border: '1px solid #ccc',
                              backgroundColor: '#fff'
                            }}
                            onChange={e => {
                              // Allow empty value for input flexibility
                              setEditedPageNumber(e.target.value);
                            }}
                            onKeyDown={handlePageNumberKeyDown}
                            onBlur={handlePageNumberSave}
                          />
                        </div>
                      ) : (
                        <div
                          className="page-number-badge"
                          style={{
                            fontSize: '0.85rem',
                            color: '#666',
                            backgroundColor: '#f3f4f6',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            marginLeft: '8px',
                            cursor: 'pointer'
                          }}
                          onClick={() =>
                            handlePageNumberClick(editedQuestion.subquestions[0].page_number)
                          }
                        >
                          Page {editedQuestion.subquestions[0].page_number || 1}
                        </div>
                      )}

                      {/* Edit with AI Button */}
                      <button
                        className="edit-ai-btn"
                        style={{
                          marginLeft: 'auto',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '5px 10px',
                          borderRadius: '4px',
                          color: '#fff',
                          background: 'linear-gradient(to right, #e4b17a, #c99b69)',
                          border: 'none',
                          fontWeight: '500',
                          fontSize: '0.85rem',
                          cursor: 'pointer',
                          transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
                        }}
                        onClick={() => handleAiEditClick(0)}
                      >
                        <Sparkles
                          size={18}
                          style={{ marginRight: '6px', color: '#fff', flexShrink: 0 }}
                        />
                        Edit with AI
                      </button>
                    </div>
                    <div className="solution-content">
                      <EditableText
                        text={editedQuestion.subquestions[0].solution}
                        className="solution-content-text"
                        placeholder="Enter grading criteria..."
                        multiline={true}
                        onSave={newSolution => {
                          const updatedSubquestions = [...editedQuestion.subquestions];
                          updatedSubquestions[0].solution = newSolution;
                          const updatedQuestion = {
                            ...editedQuestion,
                            subquestions: updatedSubquestions
                          };
                          setEditedQuestion(updatedQuestion);
                          handleSave(updatedQuestion);
                        }}
                      />
                      <div className="points-control">
                        <button
                          className="points-control-btn points-decrease"
                          onClick={() => {
                            const updatedSubquestions = [...editedQuestion.subquestions];
                            const currentWeight = updatedSubquestions[0].weight || 1;
                            if (currentWeight > 0.5) {
                              updatedSubquestions[0].weight = currentWeight - 0.5;
                              updatedSubquestions[0].max_weight = currentWeight - 0.5;
                              const updatedQuestion = {
                                ...editedQuestion,
                                subquestions: updatedSubquestions
                              };
                              setEditedQuestion(updatedQuestion);
                              handleSave(updatedQuestion);
                            }
                          }}
                        >
                          -
                        </button>
                        <span className="points-display">
                          {editedQuestion.subquestions[0].weight || 1}
                        </span>
                        <button
                          className="points-control-btn points-increase"
                          onClick={() => {
                            const updatedSubquestions = [...editedQuestion.subquestions];
                            const currentWeight = updatedSubquestions[0].weight || 1;
                            updatedSubquestions[0].weight = currentWeight + 0.5;
                            updatedSubquestions[0].max_weight = currentWeight + 0.5;
                            const updatedQuestion = {
                              ...editedQuestion,
                              subquestions: updatedSubquestions
                            };
                            setEditedQuestion(updatedQuestion);
                            handleSave(updatedQuestion);
                          }}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )
            ) : (
              <div></div>
            )}
          </>
        )}
      </div>

      {/* AI Edit Popup */}
      {showAiEditPopup && (
        <div
          className="ai-edit-popup-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
          onClick={e => {
            if (e.target === e.currentTarget && !isProcessingAiEdit) {
              setShowAiEditPopup(false);
            }
          }}
        >
          <div
            className="ai-edit-popup"
            style={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              padding: '20px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              width: '400px',
              maxWidth: '90vw',
              display: 'flex',
              flexDirection: 'column',
              gap: '15px'
            }}
          >
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Sparkles size={20} style={{ color: '#e4b17a' }} />
              Edit Grading Criteria with AI
            </h3>
            <textarea
              ref={aiEditInputRef}
              value={aiEditInput}
              placeholder="Type your request here, e.g. 'Make the grading criteria more detailed' or 'Add a criteria for partial credit'"
              style={{
                padding: '10px 12px',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                resize: 'vertical',
                minHeight: '100px',
                fontSize: '14px',
                fontFamily: 'inherit'
              }}
              disabled={isProcessingAiEdit}
              onChange={e => setAiEditInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && e.ctrlKey && !isProcessingAiEdit) {
                  handleAiEditConfirm();
                } else if (e.key === 'Escape' && !isProcessingAiEdit) {
                  setShowAiEditPopup(false);
                }
              }}
            />

            {/* Toggle for applying to all questions */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginTop: '-5px',
                cursor: isProcessingAiEdit ? 'not-allowed' : 'pointer'
              }}
              onClick={() => !isProcessingAiEdit && setApplyToAllQuestions(!applyToAllQuestions)}
            >
              <div
                style={{
                  width: '36px',
                  height: '20px',
                  backgroundColor: applyToAllQuestions ? '#e4b17a' : '#d1d5db',
                  borderRadius: '10px',
                  position: 'relative',
                  transition: 'background-color 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                  marginRight: '10px',
                  opacity: isProcessingAiEdit ? 0.6 : 1
                }}
              >
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    position: 'absolute',
                    top: '2px',
                    left: applyToAllQuestions ? '18px' : '2px',
                    transition: 'left 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                />
              </div>
              <span
                style={{
                  fontSize: '14px',
                  color: '#4b5563',
                  opacity: isProcessingAiEdit ? 0.6 : 1
                }}
              >
                Apply to each question
              </span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                style={{
                  padding: '8px 12px',
                  borderRadius: '4px',
                  border: '1px solid #d1d5db',
                  backgroundColor: '#f3f4f6',
                  cursor: isProcessingAiEdit ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  opacity: isProcessingAiEdit ? 0.6 : 1
                }}
                disabled={isProcessingAiEdit}
                onClick={() => !isProcessingAiEdit && setShowAiEditPopup(false)}
              >
                Cancel
              </button>
              <button
                style={{
                  padding: '8px 16px',
                  borderRadius: '4px',
                  border: 'none',
                  background: 'linear-gradient(to right, #e4b17a, #c99b69)',
                  color: '#fff',
                  cursor: isProcessingAiEdit ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: isProcessingAiEdit ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                disabled={isProcessingAiEdit}
                onClick={handleAiEditConfirm}
              >
                {isProcessingAiEdit ? (
                  <>
                    <span
                      className="spinner"
                      style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderRadius: '50%',
                        borderTopColor: '#fff',
                        animation: 'spin 1s linear infinite'
                      }}
                    ></span>
                    Processing...
                  </>
                ) : (
                  'Confirm'
                )}
              </button>
            </div>
            <style jsx>{`
              @keyframes spin {
                to {
                  transform: rotate(360deg);
                }
              }
            `}</style>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionCard;
